/* eslint-disable @reatom/unit-naming-rule */
import { action, atom } from '@reatom/framework'
import BigNumber from 'bignumber.js'

import { Result } from '@blade/std-lib'
import { userAtom } from '@root/entities/auth'
import { processBuySwapAction } from '@root/features/trade/swap'
import { tradeSettingsAtom } from '@root/features/trade/trade-settings'
import { tradingWalletAtom } from '@root/features/trade/trading'
import { reatomMarketBuyControls } from '@root/features/trade/trading-controls/atoms/common/buy'
import { Error } from '@root/shared'

import { reatomInstantBuyQuote } from './reatom-instant-buy-quote'

import type { PairModel } from '@root/entities/pair/atoms'

export const reatomInstantBuy = ({ pair }: { pair: PairModel }) => {
  const marketControls = reatomMarketBuyControls()
  const instantBuyQuotesModel = reatomInstantBuyQuote({ pair })

  const quickBuyAction = action(async (ctx, amount: BigNumber) => {
    const wallet = ctx.get(tradingWalletAtom)

    const settings = ctx.get(tradeSettingsAtom)
    const quotes = ctx.get(instantBuyQuotesModel)
    const quoteModel = quotes.find((quote) =>
      quote.amount.eq(amount),
    )?.quoteModel

    const quote = quoteModel ? ctx.get(quoteModel.quote) : undefined

    if (amount.eq(0) || !wallet) {
      return Result.fail(Error.UnknownError.fromUnknown('invalid input'))
    }

    return processBuySwapAction(ctx, {
      amount: amount,
      pair: pair,
      quote,
      settings: settings,
      wallet: wallet,
    })
  })

  const buyAction = action(async (ctx) => {
    const amount = ctx.get(marketControls.amountInputModel.amountAtom)
    return quickBuyAction(ctx, amount)
  })

  const initializeAction = action((ctx) => {
    instantBuyQuotesModel.subscribe(ctx)
    return () => {
      instantBuyQuotesModel.unsubscribe(ctx)
    }
  })

  return {
    ...marketControls,
    initialize: initializeAction,
    quickBuyAction,
    buyAction,
  }
}

export type InstantBuyModel = ReturnType<typeof reatomInstantBuy>

const INSTANT_BUY_QUICK_OPTIONS: InstantBuyQuickOptions = [
  BigNumber(0.01),
  BigNumber(0.02),
  BigNumber(0.5),
  BigNumber(1),
]

type InstantBuyQuickOptions = readonly [
  BigNumber,
  BigNumber,
  BigNumber,
  BigNumber,
]

export const instantBuyQuickOptionsAtom = atom((ctx) => {
  const user = ctx.spy(userAtom)

  return INSTANT_BUY_QUICK_OPTIONS.map((x, i) => {
    return user?.instantTradeButtons.buyButtons[i]?.amount ?? x
  }) as unknown as InstantBuyQuickOptions
}, 'instantBuyQuickOptionsAtom')
